import type { AddOnType } from '../addOnType';

export const BATCH_COLUMNS = [
    'title',
    'page',
    'content',
    'pretitle',
    'author',
    'byLine',
    'imageUrl',
    'imageCaption',
    'type',
    'insertAs',
] as const;

export type BatchColumn = (typeof BATCH_COLUMNS)[number];

const REQUIRED_COLUMNS: readonly BatchColumn[] = ['title', 'page'];

export type InsertAs = 'embed' | 'link';

/** "Número de página", "numero_de_pagina" and "NUMERO DE PAGINA" are one name. */
export function normalizeHeader(raw: string): string {
    return (raw ?? '')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .toLowerCase()
        .replace(/[^\p{L}\p{N}]+/gu, '_')
        .replace(/^_+|_+$/g, '');
}

// The sheet is in the operator's language, not necessarily the reader's. The
// reader's own labels and the API's field names are here too. Variants of
// accent, case and punctuation are not listed, since they normalize alike.
export const HEADER_ALIASES: Record<BatchColumn, readonly string[]> = {
    title: [
        'title',
        'headline',
        'título',
        'título del add-on',
        'titular',
        'titolo',
        'titre',
        'titel',
        'tytuł',
        'rubrik',
    ],
    page: [
        'page',
        'page number',
        'start page',
        'página',
        'pág',
        'número de página',
        'página de inicio',
        'numero di pagina',
        'numéro de page',
        'sida',
        'sidnummer',
        'número da página',
        'seite',
        'seitenzahl',
        'strona',
        'numer strony',
        'side',
        'sidetal',
        'sidenummer',
    ],
    content: [
        'content',
        'body',
        'text',
        'link',
        'url',
        'media',
        'contenido',
        'cuerpo',
        'texto',
        'enlace',
        'contenuto',
        'testo',
        'contenu',
        'texte',
        'innehåll',
        'brödtext',
        'conteúdo',
        'corpo',
        'inhalt',
        'treść',
        'tekst',
        'indhold',
    ],
    pretitle: [
        'pretitle',
        'kicker',
        'topic',
        'volanta',
        'antetítulo',
        'tema',
        'sopratitolo',
        'occhiello',
        'argomento',
        'surtitre',
        'thème',
        'överrubrik',
        'ämne',
        'chapéu',
        'dachzeile',
        'thema',
        'nadtytuł',
        'temat',
        'emne',
    ],
    author: ['author', 'autor', 'firma', 'autore', 'auteur', 'författare', 'forfatter'],
    byLine: [
        'by line',
        'byline',
        'subtitle',
        'standfirst',
        'bajada',
        'subtítulo',
        'sottotitolo',
        'catenaccio',
        'sous-titre',
        'chapô',
        'underrubrik',
        'ingress',
        'linha fina',
        'linha de apoio',
        'untertitel',
        'unterzeile',
        'podtytuł',
        'undertitel',
    ],
    imageUrl: [
        'image',
        'image url',
        'image from url',
        'featured image',
        'banner',
        'imagen',
        'imagen destacada',
        'url de la imagen',
        'immagine',
        'immagine in evidenza',
        'url dell’immagine',
        'image à la une',
        'url de l’image',
        'bild',
        'bild-url',
        'utvald bild',
        'imagem',
        'imagem destacada',
        'url da imagem',
        'beitragsbild',
        'obraz',
        'url obrazu',
        'obraz wyróżniający',
        'billede',
        'billed-url',
        'udvalgt billede',
    ],
    imageCaption: [
        'caption',
        'image caption',
        'epígrafe',
        'epígrafe de la imagen',
        'pie de foto',
        'texto de la imagen',
        'didascalia',
        'didascalia dell’immagine',
        'légende',
        'légende de l’image',
        'bildtext',
        'legenda',
        'texto da imagem',
        'legenda da imagem',
        'bildunterschrift',
        'podpis',
        'podpis obrazu',
        'billedtekst',
    ],
    type: ['icon', 'type', 'ícono', 'tipo', 'icona', 'icône', 'ikon', 'symbol', 'ikona', 'typ'],
    insertAs: [
        'insert as',
        'mode',
        'insertar como',
        'modo',
        'inserisci come',
        'insérer comme',
        'infoga som',
        'inserir como',
        'einfügen als',
        'wstaw jako',
        'indsæt som',
    ],
};

export const INSERT_AS_ALIASES: Record<InsertAs, readonly string[]> = {
    embed: [
        'embed',
        'embedded',
        'iframe',
        'player',
        'embebido',
        'incrustado',
        'insertado',
        'reproductor',
        'incorporato',
        'intégré',
        'inbäddad',
        'incorporado',
        'eingebettet',
        'osadzony',
        'indlejret',
    ],
    link: ['link', 'enlace', 'vínculo', 'collegamento', 'lien', 'länk', 'verknüpfung', 'łącze'],
};

/** The names the reader's icon picker gives each type in every language, beside the stored codes. */
export const TYPE_ALIASES: Record<AddOnType, readonly string[]> = {
    article: ['article', 'artículo', 'articolo', 'artikel', 'artigo', 'artykuł'],
    video: ['video', 'wideo'],
    audio: ['audio', 'ljud', 'lyd', 'sound'],
    '3d': ['3d'],
    link: ['link', 'enlace', 'lien', 'länk'],
    image: ['image', 'imagen', 'immagine', 'bild', 'imagem', 'obraz', 'billede'],
};

/** Reads a cell through a table of aliases. A collision is a bug the tests catch. */
function finder<K extends string>(aliases: Record<K, readonly string[]>): (cell: string) => K | null {
    const table = new Map<string, K>();
    for (const key of Object.keys(aliases) as K[]) {
        for (const alias of aliases[key]) table.set(normalizeHeader(alias), key);
    }
    return cell => table.get(normalizeHeader(cell)) ?? null;
}

export const columnForHeader = finder(HEADER_ALIASES);
export const insertAsFor = finder(INSERT_AS_ALIASES);
export const typeFor = finder(TYPE_ALIASES);

export interface ColumnMapping {
    /** The column each position of a row holds, or null for one the batch does not read. */
    byIndex: (BatchColumn | null)[];
    /** The headers the batch leaves out, as the operator wrote them. */
    unrecognized: string[];
    missing: BatchColumn[];
}

/** Columns come in any order. A repeated one is read from its first position, and the others are left out. */
export function mapHeader(header: readonly string[]): ColumnMapping {
    const seen = new Set<BatchColumn>();
    const unrecognized: string[] = [];

    const byIndex = header.map(cell => {
        const column = columnForHeader(cell);
        if (!column || seen.has(column)) {
            if (cell.trim()) unrecognized.push(cell.trim());
            return null;
        }

        seen.add(column);
        return column;
    });

    return { byIndex, unrecognized, missing: REQUIRED_COLUMNS.filter(column => !seen.has(column)) };
}
